import { useSelector } from "react-redux";
import { getPhoto, getSessionId, getTableNumber, getName, updateCreateResult, getBookingType } from "../redux/slices";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loading } from "./Loading";
import "./playerEntryPhotoConfirm.scss";
import { CreatePlayerDto, PlayerCreateResponse } from "../types/player";
import { useAppDispatch } from "../redux/hooks";
import { trackEvent } from "../utils/analytics";

export const PlayerEntryPhotoConfirm = () => {
  const name = useSelector(getName);
  const photo = useSelector(getPhoto);
  const sessionId = useSelector(getSessionId);
  const tableNumber = useSelector(getTableNumber);
  const bookingType = useSelector(getBookingType);
  const dispatch = useAppDispatch();
  const nav = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);

  // BEGIN copied link checking

  const [searchParams] = useSearchParams();

  // if the redux store is missing a session ID (or the session ID does not match the URL parameter),
  // then redirect to the start page. Suspect that this is a shared link and this "new" user needs to start at the beginning of the journey
  let sessionIdParam = searchParams.get("s");
  let tableNumberParam = searchParams.get("t");
  let venueCode = searchParams.get("v");

  useEffect(() => {
    if (sessionId === null || tableNumber === null) {
      nav(`/?s=${sessionIdParam}&t=${tableNumberParam}&v=${venueCode}`);
    }

    if (sessionId !== sessionIdParam || tableNumber !== tableNumberParam) {
      nav(`/?s=${sessionIdParam}&t=${tableNumberParam}&v=${venueCode}`);
    } else {
      setLoading(false);
    }
  }, [sessionId, tableNumber, venueCode, sessionIdParam, tableNumberParam, nav]);

  // END of "copied link" checking

  // send back to take another photo
  const retake = () => {
    trackEvent({
      gaEvent: "photo_retaken_click_retake",
      params: { venue_code: venueCode },
    });
    nav(`/photo?s=${sessionId}&t=${tableNumber}&v=${venueCode}&retake=true`);
  };

  const submit = async () => {
    // wrap name a photo in the player DTO and send it to the server
    const playerDTO: CreatePlayerDto = {
      Name: name,
      Photo: photo,
    };

    setLoading(true);

    trackEvent({
      gaEvent: "click_accept_photo",
      clarityEvent: "click_accept_photo",
      params: { venue_code: venueCode },
    });

    // POST the created player DTO to the CreatePlayer endpoint
    try {
      const response: Response = await fetch(`/api/CreatePlayer/${sessionId}?t=${tableNumber}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(playerDTO),
      });

      if (response.ok) {
        const createResult: PlayerCreateResponse = await response.json();
        dispatch(updateCreateResult(createResult));
        trackEvent({
          gaEvent: "PlayerEntered",
          clarityEvent: "player_joined_game",
          params: { venue_code: venueCode, booking_type: bookingType },
        });
      } else {
        dispatch(
          updateCreateResult({
            id: "",
            createSuccess: false,
            eventType: 0,
            teamName: null,
            ocheNumber: 0,
            reason: "Something went wrong",
          }),
        );
      }
      setLoading(false);
      nav(`/details?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
    } catch (error) {
      dispatch(
        updateCreateResult({
          id: "",
          createSuccess: false,
          eventType: 0,
          teamName: null,
          ocheNumber: 0,
          reason: "Something went wrong",
        }),
      );
      setLoading(false);
      nav(`/details?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
    }
    nav(`/details?s=${sessionId}&t=${tableNumber}&v=${venueCode}`);
  };

  if (loading) return <Loading />;

  return (
    <div className="pageContainer cameraPage">
      <div className="logoContainer">
        <img src={"./img/fc-logo.svg"} width="90px" alt="Flight Club" />
      </div>

      <div className="formHeader cameraHeader">
        <div className="title">
          Game face on. <br /> Aim true.
        </div>
      </div>

      <div className="circlePhotoWrapper">
        <div className={`circlePhotoContainer${photo ? " captured" : ""}`}>
          {photo && <img src={photo} alt="Captured" className="circleWebcam" />}
        </div>
        <button
          className="captureBtn retakeBtn"
          onClick={retake}
          aria-label="Retake photo"
          disabled={photo === null}
        >
          <img src={"./img/retake-button.svg"} alt="Retake" />
        </button>
      </div>

      <footer className="btn-container no-margin">
        <button className="secondary small with-back-arrow" disabled={photo === null} onClick={retake}>
          <img className="previous-arrow" src="./img/previous-arrow.svg" alt="" />
          BACK
        </button>
        <button className="small with-forward-arrow" disabled={photo === null} onClick={submit}>
          CONFIRM
          <img className="forward-arrow" src="./img/forward-arrow.svg" alt="" />
        </button>
      </footer>
    </div>
  );
};
